import React, { useState, useEffect } from "react";
import {
  cadastrarCorretor,
  obterCorretores,
  removerCorretor,
  editarCorretor,
} from "../../services/corretoresService";

import "../styles/Cadastrar.css";

import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";

import { FaPencilAlt, FaTrashAlt } from "react-icons/fa";
import { Add as AddIcon } from "@mui/icons-material";

const ADM_IDS = [
  "bCLFZqvJ1oRRo8QauANwzXNUGdp1", // Aluizio
  "jfdv2ulJQsQaRDmoUS8DhxA87ow1", // Vitor
  "vGbj9YB3WVTih3kY5lqKtNcJLYl2", // e2tcode
];

const CadastrarCorretor = () => {
  const [corretores, setCorretores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  // 🔔 Toast Notification
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

  const [formData, setFormData] = useState({
    creci: "",
    nome: "",
    sobrenome: "",
    telefone: "",
    email: "",
    senha: "",
  });

  const isAdmin = ADM_IDS.includes(localStorage.getItem("corretorID"));

  // 🔹 Carregar corretores
  useEffect(() => {
    fetchCorretores();
  }, []);

  const fetchCorretores = async () => {
    setLoading(true);
    try {
      const lista = await obterCorretores();
      setCorretores(lista);
    } catch (error) {
      console.error("Erro ao buscar corretores:", error);
      showToast("Erro ao carregar corretores.", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Handlers gerais
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setSelectedImage(file);
      setImagePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setFormData({
      creci: "",
      nome: "",
      sobrenome: "",
      telefone: "",
      email: "",
      senha: "",
    });
    setSelectedImage(null);
    setImagePreview(null);
    setEditandoId(null);
  };

  // 🔹 Ações
  const handleSubmit = async (e) => {
    e.preventDefault();
    const { creci, nome, sobrenome, telefone, email, senha } = formData;

    if (!creci || !nome || !sobrenome || !telefone || !email || (!editandoId && !senha)) {
      showToast("Preencha todos os campos obrigatórios.", "warning");
      return;
    }

    try {
      setSaving(true);
      if (editandoId) {
        await editarCorretor(editandoId, creci, nome, sobrenome, telefone, email, selectedImage);
        showToast("Corretor atualizado com sucesso!", "success");
      } else {
        await cadastrarCorretor(creci, nome, sobrenome, telefone, email, senha, selectedImage);
        showToast("Corretor cadastrado com sucesso!", "success");
      }
      resetForm();
      setOpenDialog(false);
      fetchCorretores();
    } catch (error) {
      showToast("Erro ao salvar corretor. Verifique os dados e tente novamente.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (corretor) => {
    setFormData({
      creci: corretor.creci,
      nome: corretor.nome,
      sobrenome: corretor.sobrenome,
      telefone: corretor.telefone,
      email: corretor.email,
      senha: "",
    });
    setImagePreview(corretor.fotoURL);
    setEditandoId(corretor.id);
    setOpenDialog(true);
  };

  const handleDelete = async (userId) => {
    if (!isAdmin) return showToast("Apenas administradores podem remover corretores.", "warning");
    if (window.confirm("Tem certeza que deseja remover este corretor?")) {
      try {
        await removerCorretor(userId);
        fetchCorretores();
        showToast("Corretor removido com sucesso!", "info");
      } catch (error) {
        showToast("Erro ao remover corretor. Tente novamente.", "error");
      }
    }
  };

  // ===================== RENDER =====================
  return (
    <div className="containerCadastro">
      <Box p={{ xs: 2, md: 4 }}>
        <Box display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent={{ xs: "center", sm: "space-between" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          mb={3}
          gap={2}>
          <Typography variant="h4" fontWeight="bold">
            Corretores Cadastrados ({corretores.length})
          </Typography>

          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpenDialog(true);
              }}
              sx={{
                backgroundColor: "var(--verde)",
                "&:hover": { backgroundColor: "var(--bege)" },
              }}
            >
              Cadastrar Novo Corretor
            </Button>
          )}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" mt={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid
            container
            spacing={2}
            justifyContent="flex-start"
            alignItems="stretch" // 🔹 mantém altura uniforme dos cards
          >
            {corretores.map((corretor) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={corretor.id}>
                <Card
                  sx={{
                    height: "100%", // 🔹 garante tamanho uniforme
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    boxShadow: 3,
                    borderRadius: 2,
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: 5,
                    },
                  }}
                >
                  {/* HEADER */}
                  <Box display="flex" alignItems="center" gap={2} p={2}>
                    <Avatar
                      src={corretor.fotoURL}
                      alt={corretor.nome}
                      sx={{
                        width: 58,
                        height: 58,
                        border: "2px solid var(--verde)",
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {corretor.nome} {corretor.sobrenome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        CRECI: {corretor.creci}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {corretor.telefone}
                      </Typography>
                    </Box>
                  </Box>

                  {/* BODY */}
                  <CardContent sx={{ pt: 0 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        wordBreak: "break-word",
                      }}
                    >
                      Email: {corretor.email}
                    </Typography>
                  </CardContent>

                  {/* FOOTER */}
                  {isAdmin && (
                    <CardActions
                      sx={{
                        justifyContent: "flex-end",
                        pb: 1,
                        pr: 1.5,
                      }}
                    >
                      <IconButton
                        onClick={() => handleEdit(corretor)}
                        sx={{
                          color: "var(--verde)",
                          "&:hover": { opacity: 0.8 },
                        }}
                      >
                        <FaPencilAlt />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(corretor.id)}
                        sx={{
                          color: "#cf4848",
                          "&:hover": { opacity: 0.8 },
                        }}
                      >
                        <FaTrashAlt />
                      </IconButton>
                    </CardActions>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* === DIALOG DE CADASTRO/EDIÇÃO === */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: "bold", color: "var(--verde)" }}>
            {editandoId ? "Editar Corretor" : "Cadastrar Novo Corretor"}
          </DialogTitle>

          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} display="flex" justifyContent="center">
                <Avatar
                  src={imagePreview}
                  sx={{ width: 90, height: 90, border: "2px solid var(--verde)" }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{
                    color: "var(--verde)",
                    borderColor: "var(--verde)",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                  }}
                >
                  Escolher Foto
                  <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                </Button>
              </Grid>

              <Grid item xs={6}>
                <TextField label="CRECI" fullWidth name="creci" value={formData.creci} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Nome" fullWidth name="nome" value={formData.nome} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Sobrenome" fullWidth name="sobrenome" value={formData.sobrenome} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Telefone" fullWidth name="telefone" value={formData.telefone} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Email" fullWidth name="email" value={formData.email} onChange={handleChange} />
              </Grid>

              {!editandoId && (
                <Grid item xs={12}>
                  <TextField label="Senha" type="password" fullWidth name="senha" value={formData.senha} onChange={handleChange} />
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} sx={{ color: "var(--verde)" }}>Cancelar</Button>

            <Button
              variant="contained"
              disabled={saving}
              onClick={handleSubmit}
              sx={{
                backgroundColor: "var(--verde)",
                "&:hover": { backgroundColor: "var(--bege)" },
                minWidth: 130,
              }}
            >
              {saving ? (
                <CircularProgress size={22} color="inherit" />
              ) : editandoId ? (
                "Salvar Alterações"
              ) : (
                "Cadastrar"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* === SNACKBAR / TOAST === */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%", fontWeight: "bold", backgroundColor: "var(--verde)" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CadastrarCorretor;
